import { Link } from 'react-router-dom'
import PublicHeader from '@/components/PublicHeader'
import PublicFooter from '@/components/PublicFooter'

export default function HowToUsePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      <PublicHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8 animate-fade-in">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">
              How to Use <span className="gradient-text">GistMag</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Get started with AI-powered alt text generation in minutes
            </p>
          </div>

          {/* Quick Start */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Quick Start</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 dark:bg-primary-500 text-white flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Create an Account</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-3">
                    Sign up for a free account to get started. You'll receive 50 free credits to try out the service.
                  </p>
                  <Link
                    to="/auth/signup"
                    className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
                  >
                    <span>Sign Up Now</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 dark:bg-primary-500 text-white flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Get Your API Key</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-3">
                    Once logged in, navigate to the API Key page to generate your unique API key. This key is used 
                    to authenticate all API requests.
                  </p>
                  <Link
                    to="/dashboard/api-key"
                    className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
                  >
                    <span>Get API Key</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 dark:bg-primary-500 text-white flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Generate Alt Text</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Use the dashboard to upload images and generate alt text, or integrate our API into your 
                    application. Each generation uses 1 credit.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Using the Dashboard */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Using the Dashboard</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Single Image Generation</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  On the home page, you can upload a single image and generate alt text instantly. Choose from 
                  three modes:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 ml-4">
                  <li><strong>Short:</strong> Concise, descriptive alt text (recommended for most use cases)</li>
                  <li><strong>Long:</strong> Detailed descriptions with more context</li>
                  <li><strong>SEO:</strong> Optimized for search engines while remaining accessible</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Batch Processing</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Use the Batch Generator to process multiple images at once. Perfect for large websites or 
                  content migrations. Upload up to 100 images at a time and generate alt text for all of them 
                  in one go.
                </p>
              </div>
            </div>
          </div>

          {/* Using the API */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Using the API</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Basic Request</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  Make a POST request to our API endpoint with your API key and image URL:
                </p>
                <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-gray-100">
{`POST https://api.gistmag.co.uk/generate
Content-Type: application/json

{
  "api_key": "atg_your_api_key_here",
  "image_url": "https://example.com/image.jpg",
  "mode": "short",
  "language": "en"
}`}
                  </pre>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Response</h3>
                <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-gray-100">
{`{
  "alt_text": "A person typing on a laptop",
  "short_alt_text": "Person typing on laptop",
  "long_alt_text": "A person sitting at a desk typing on a laptop computer",
  "seo_alt_text": "Professional working on laptop computer",
  "image_url": "https://example.com/image.jpg",
  "generated_at": "2025-01-08T12:00:00Z"
}`}
                  </pre>
                </div>
              </div>
              <div>
                <Link
                  to="/docs"
                  className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
                >
                  <span>View Full API Documentation</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>

          {/* Integrations */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Integrations</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <img src="/make-color.png" alt="Make.com" className="w-6 h-6 mr-2 object-contain" />
                  Make.com
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  Automate alt text generation in your Make.com workflows. Connect GistMag to Google Drive, WordPress, 
                  Shopify, and hundreds of other apps.
                </p>
                <Link
                  to="/make"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold inline-flex items-center gap-1"
                >
                  Learn More <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <img src="/wordpress-logo3.webp" alt="WordPress" className="w-6 h-6 mr-2 object-contain" />
                  WordPress
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  Install our WordPress plugin to automatically generate alt text for images. Works with the 
                  media library, post editor, and featured images.
                </p>
                <div className="flex items-center gap-4">
                  <a
                    href="https://wordpress.org/plugins/gistmag-alt-text-generator/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold inline-flex items-center gap-1"
                  >
                    Install from WordPress.org <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <img src="/shopify.png" alt="Shopify" className="w-6 h-6 mr-2 object-contain" />
                  Shopify
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  Integrate GistMag with your Shopify store to automatically generate alt text for product 
                  images and improve your store's accessibility.
                </p>
                <span className="text-gray-500 dark:text-gray-500 font-semibold">
                  Coming Soon
                </span>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200/50 dark:border-slate-700/50">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                Browser Extensions
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Install our browser extension to generate alt text for any image on the web with a simple right-click.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <a
                  href="https://chromewebstore.google.com/detail/gistmag-alt-text-generato/fdlepmdamhcpnlnkpmpkgdhfelogkahi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition"
                >
                  <img src="/Google-Chrome-Logo-for-tech-themed-designs-web-development-graphics-transparent-PNG-image-jpg.png" alt="Chrome" className="w-5 h-5 object-contain" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Chrome</span>
                  <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <a
                  href="https://microsoftedge.microsoft.com/addons/detail/gistmag-alt-text-generato/piejclnjcjmlgneiecnnjgmnknlbpbje"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition"
                >
                  <img src="/png-clipart-edge-browser-logo-tech-companies-thumbnail.png" alt="Edge" className="w-5 h-5 object-contain" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Edge</span>
                  <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <a
                  href="https://addons.mozilla.org/en-GB/firefox/addon/gistmag-alt-text-generator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition"
                >
                  <img src="/firefox.png" alt="Firefox" className="w-5 h-5 object-contain" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Firefox</span>
                  <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Best Practices */}
          <div className="glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Best Practices</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Choose the Right Mode</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Use <strong>Short</strong> mode for most images, <strong>Long</strong> for complex images that need 
                  detailed descriptions, and <strong>SEO</strong> when optimizing for search engines.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Review Generated Alt Text</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  While our AI is highly accurate, always review generated alt text to ensure it accurately 
                  describes your image and fits your content's context.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Monitor Your Usage</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Keep track of your credit usage in the Analytics page. Credits rollover indefinitely, so 
                  you can use them at your own pace.
                </p>
              </div>
            </div>
          </div>

          {/* Need Help */}
          <div className="text-center glass-card rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Need More Help?</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Check out our FAQ section or contact our support team
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => {
                  window.location.href = '/#faq-section'
                }}
                className="px-8 py-3 bg-primary-600 dark:bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition hover-lift"
              >
                View FAQ
              </button>
              <Link
                to="/about"
                className="px-8 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 transition hover-lift border border-gray-300 dark:border-slate-700"
              >
                Learn About GistMag
              </Link>
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}

